import type { TaskType } from '@/types/task';
import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';

/** One of the technician's appointments for today (§9.1). */
export interface MyDayAppointment {
  id: string;
  customerName: string | null;
  status: string;
  /** DISPATCHED / IN_PROGRESS / ON_HOLD — an appointment the tech may ping for. */
  active: boolean;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  /**
   * §11.4 — the customer's promised arrival window (when one applies). The tech
   * still sees the precise `scheduledStart`; this is shown alongside it.
   */
  arrivalWindow: { start: string; end: string } | null;
  address: string | null;
  hasCoords: boolean;
  arrivedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

/** A task on the technician's day: due today, or overdue and still open. */
export interface MyDayTask {
  id: string;
  title: string;
  description: string | null;
  type: TaskType;
  status: string;
  priority: string;
  dueDate: string | null;
  allDay: boolean;
  durationMinutes: number | null;
  overdue: boolean;
  /** The address picked on the task itself; null when none was. */
  address: string | null;
  contact: { id: string; name: string; phone: string | null };
}

export interface MyDay {
  technicianId: string;
  timezone: string;
  sharingPaused: boolean;
  lastSharedAt: string | null;
  /** Absent from an operations build older than tasks on My Day. */
  tasks?: MyDayTask[];
  appointments: MyDayAppointment[];
}

export const myDayQueryKey = ['tracking', 'me', 'day'] as const;

/**
 * The signed-in technician's day: today's appointments + sharing state. Drives
 * the "My Day" emitter surface. `showErrorToast` is off — a non-technician gets
 * a 403 here by design, which the page renders as an unavailable state.
 */
export function useMyDay(enabled = true) {
  return useApiQuery<MyDay>({
    queryKey: myDayQueryKey,
    queryFn: () => apiGet<MyDay>(operationsClient, schedulerEndpoints.myTrackingDay()),
    enabled,
    showErrorToast: false,
    refetchInterval: 60_000,
  });
}
